/**
 * The event shape the site renders, and the helper that orders it. Events
 * themselves come only from published content — see publishedEvents.ts. The
 * hand-kept catalogue that used to live here is gone: every one of its events
 * is published through webadmin now.
 */

import type { Media } from "@zivin-labs/site-content";

export interface EventScheduleItem {
  time: string;
  what: string;
  who?: string;
}

export interface EventBodyBlock {
  heading?: string;
  text?: string;
  list?: string[];
}

export interface EventItem {
  slug: string;
  tag: string;
  title: string;
  /** Compact human-readable date line, e.g. "24 May 2023". */
  dateLine: string;
  /** Decomposed pieces for the typographic hero and index date blocks. */
  dateBig: { day: string; month: string; year: string };
  /** Only where the source states one. */
  time?: string;
  venue: string;
  /** Placeholder label until a photograph is ingested for the event. */
  hero: string;
  /** The event's photograph, where the institute publishes one. */
  media?: Media;
  /** ~160-char summary shown in card and hero. */
  summary: string;
  /**
   * Set when `summary` is the event's whole description rather than a short
   * blurb (published events have only a description). The card still shows it;
   * the detail page puts the description in the body and leaves the banner out.
   */
  summaryIsDescription?: boolean;
  body: EventBodyBlock[];
  schedule?: EventScheduleItem[];
  organizers?: string[];
  registrationDeadline?: string;
  /** Optional registration URL — external. Disabled if absent. */
  registrationUrl?: string;
  /** Department code if this event is owned by one specific department. */
  departmentCode?: string;
  status: "upcoming" | "past";
  /** Where the institute publishes this event. */
  source: string;
  /** From webadmin's own `featured` flag (see publishedEvents.ts); drives the home page's Campus Events rail. */
  featured?: boolean;
}

/** Reconstructs a sortable instant from the decomposed date block — the only shape either source guarantees. */
export function eventTimestamp(e: EventItem): number {
  return Date.parse(`${e.dateBig.day} ${e.dateBig.month} ${e.dateBig.year}`);
}
